/**
 * La cuenta de un producto, para lo que se enseña en pantalla.
 *
 * Es el reflejo de `backend/src/services/economia.service.js`, que es la
 * autoridad: lo que se guarda en la base de datos lo calcula el servidor. Aquí
 * solo se calcula lo que aún no existe en ninguna parte — la estimación de un
 * candidato que todavía no has importado, y la vista previa mientras mueves el
 * margen en el editor. Si cambias la fórmula, cambia las dos.
 *
 * En dropshipping el flete lo paga quien vende, así que la ganancia es
 * `precio − producto − envío`. Envío `null` significa que no se conoce, y no es
 * lo mismo que cero: cero sería envío gratis.
 *
 * A esa resta le faltaba la comisión del destino, así que hay dos ganancias:
 * `ganancia` (tras el proveedor) y `gananciaNeta` (tras la comisión). La segunda
 * es `null` mientras no se sepa a qué destino va el producto.
 */

export const MARGEN_POR_DEFECTO = 30;

function aCentavos(n) {
    return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function aNumeroONulo(valor) {
    if (valor === null || valor === undefined || valor === '') return null;
    const n = Number(valor);
    return Number.isFinite(n) ? n : null;
}

export function calcularEconomia({
    costoProducto,
    costoEnvio = null,
    margen = MARGEN_POR_DEFECTO,
    precioVenta = null,
    comision = null
} = {}) {
    const producto = aNumeroONulo(costoProducto) ?? 0;
    const envio = aNumeroONulo(costoEnvio);
    const envioConocido = envio !== null;

    const costoTotal = aCentavos(producto + (envio ?? 0));
    const margenPct = aNumeroONulo(margen) ?? MARGEN_POR_DEFECTO;
    const fijado = aNumeroONulo(precioVenta);

    const pctComision = aNumeroONulo(comision?.porcentaje);
    const comisionConocida = pctComision !== null && pctComision < 100;
    const fijaComision = comisionConocida ? aNumeroONulo(comision?.fija) ?? 0 : 0;

    // El precio sugerido cubre la comisión: si no, el margen pedido no se
    // cumple nunca. Se despeja para que sobre exactamente ese margen.
    const objetivo = costoTotal * (1 + margenPct / 100);
    const sugerido = comisionConocida
        ? (objetivo + fijaComision) / (1 - pctComision / 100)
        : objetivo;

    const precio = fijado !== null ? aCentavos(fijado) : aCentavos(sugerido);
    const ganancia = aCentavos(precio - costoTotal);

    const importeComision = comisionConocida
        ? aCentavos((precio * pctComision) / 100 + fijaComision)
        : null;
    const gananciaNeta = comisionConocida ? aCentavos(ganancia - importeComision) : null;

    return {
        costoProducto: aCentavos(producto),
        costoEnvio: envioConocido ? aCentavos(envio) : null,
        envioConocido,
        costoTotal,
        precioVenta: precio,
        margenPct,
        ganancia,
        margenReal: precio > 0 ? aCentavos((ganancia / precio) * 100) : 0,
        gananciaCompleta: envioConocido,
        comision: importeComision,
        comisionConocida,
        comisionPct: comisionConocida ? pctComision : null,
        gananciaNeta,
        margenNetoReal:
            comisionConocida && precio > 0 ? aCentavos((gananciaNeta / precio) * 100) : null,
        cuentaCompleta: envioConocido && comisionConocida
    };
}

/** "-$1.87", no "$-1.87": el signo va delante del símbolo de moneda. */
export function dinero(n) {
    const v = Number(n) || 0;
    return `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}`;
}

export default { calcularEconomia, dinero, MARGEN_POR_DEFECTO };
